import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Layout from '../../Components/layouts';
import { notify } from '../../utils';
import { REQUIRED_ERROR } from '../../utils/constants';
import { Paths } from '../../Routes/routePaths';
import { useHistory, useParams } from 'react-router-dom';
import { CoursesServices } from '../../Services/CoursesServices';
import AppLoader from '../../Components/AppLoader';
import OutlinedSelect from 'Components/OutlinedSelect';
import commonStyles, { pageTitleStyles } from 'theme/commonStyles';
import { formatUiDateTime } from '../../utils/dateTime';
import {
    buildCoursePayload,
    createEmptyCourseForm,
    getResponseCourse,
    mapCourseToForm,
    mapReadOnlyCourseFields,
    withCourseImageCacheBust,
} from './courseHelpers';

const coursesServices = new CoursesServices();

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        margin: '0 0 24px',
    },
    heading: {
        ...pageTitleStyles,
    },
    section: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(3),
        border: '1px solid var(--dm-border, #e1e6eb)',
        borderRadius: 8,
        backgroundColor: 'var(--contentColor, #fff)',
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '1rem',
        fontWeight: 700,
    },
    helperText: {
        marginTop: theme.spacing(1),
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.78rem',
    },
    imageGrid: {
        marginTop: theme.spacing(2),
    },
    imageCard: {
        height: '100%',
        padding: theme.spacing(2),
        border: '1px solid var(--dm-border, #e1e6eb)',
        borderRadius: 8,
        boxSizing: 'border-box',
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
    },
    imageLabel: {
        display: 'block',
        marginBottom: theme.spacing(1),
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    previewImage: {
        display: 'block',
        width: '100%',
        height: 170,
        borderRadius: 6,
        objectFit: 'contain',
        backgroundColor: '#fff',
    },
    imageEmpty: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 170,
        borderRadius: 6,
        color: 'var(--dm-muted, #647184)',
        backgroundColor: '#fff',
    },
    dropzone: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 170,
        padding: theme.spacing(2),
        border: `2px dashed ${theme.palette.primary.main}`,
        borderRadius: 8,
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: 'rgba(61, 125, 221, 0.04)',
        '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
    },
    fileInput: {
        display: 'none',
    },
    selectedFile: {
        marginTop: theme.spacing(1),
        color: theme.palette.primary.main,
        fontSize: '0.78rem',
        fontWeight: 600,
        wordBreak: 'break-word',
    },
    buttonRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '20px 0',
        marginTop: 20,
    },
    button: {
        minHeight: 40,
        height: 40,
        marginLeft: 15,
        padding: '8px 16px',
        borderRadius: 5,
        color: 'white',
        fontSize: '13px',
    },
    cancelButton: {
        minHeight: 40,
        height: 40,
        marginLeft: 15,
        padding: '8px 16px',
        borderRadius: 5,
        color: 'white',
        background: '#B7B7B7',
        fontSize: '13px',
        '&:hover': {
            background: '#454343',
        },
    },
}));

const courseTypes = ['DEV', 'CUS', 'Short Course', 'moodle'];
const booleanOptions = [
    { value: '', label: '—' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

const Section = ({ title, children, className, classes }) => (
    <section className={`${classes.section} ${className || ''}`}>
        <Typography component="h2" className={classes.sectionTitle}>{title}</Typography>
        {children}
    </section>
);

const editableValue = (value) => (value == null ? '' : value);
const booleanSelectValue = (value) => {
    if (value === true || value === 1 || value === 'true') return 'true';
    if (value === false || value === 0 || value === 'false') return 'false';
    return '';
};
const readOnlyValue = (value, isDate = false, isBoolean = false) => {
    if (value == null || value === '') return '—';
    if (isBoolean && (value === true || value === 1 || value === 'true')) return 'Yes';
    if (isBoolean && (value === false || value === 0 || value === 'false')) return 'No';
    if (isDate) return formatUiDateTime(value) || '—';
    return value;
};

const AddCourses = () => {
    const classes = useStyles();
    const commonClasses = commonStyles();
    const history = useHistory();
    const { id } = useParams();
    const [loading, setLoading] = React.useState(false);
    const [pageLoading, setPageLoading] = React.useState(Boolean(id));
    const update = Boolean(id);
    const [formState, setFormState] = React.useState(createEmptyCourseForm);
    const [readOnlyDetails, setReadOnlyDetails] = React.useState(() => mapReadOnlyCourseFields());
    const [durationLabel, setDurationLabel] = React.useState('');
    const [imageUrls, setImageUrls] = React.useState({ thumbnailUrl: '', fullUrl: '', updatedAt: '' });
    const [imageDataUrl, setImageDataUrl] = React.useState('');
    const [selectedImageName, setSelectedImageName] = React.useState('');

    React.useEffect(() => {
        const getCourseDetail = async () => {
            try {
                const response = await coursesServices.getCourseDetail(id);
                const course = response && Array.isArray(response.data) ? response.data[0] : null;
                if (!course) {
                    notify('error', (response && response.message) || 'Unable to load course details');
                    return;
                }

                setFormState(mapCourseToForm(course));
                setReadOnlyDetails(mapReadOnlyCourseFields(course));
                setDurationLabel(course.duration_label || '');
                setImageUrls({
                    thumbnailUrl: course.thumbnail_url || course.thumbnail || '',
                    fullUrl: course.imageUrl || '',
                    updatedAt: course.updated_at || course.last_update_on || '',
                });
            } catch (error) {
                notify('error', 'Unable to load course details');
            } finally {
                setPageLoading(false);
            }
        };

        if (id) getCourseDetail();
    }, [id]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormState((current) => ({ ...current, [name]: value }));
    };

    const handleChange = (value, name) => {
        setFormState((current) => ({ ...current, [name]: value }));
    };

    const handleBooleanChange = (event) => {
        const { name, value } = event.target;
        handleChange(value === '' ? '' : value === 'true', name);
    };

    const loadImage = (file) => {
        if (!file) return;
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            notify('error', 'Choose a PNG or JPEG image');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result !== 'string') {
                notify('error', 'Unable to read the selected image');
                return;
            }
            setImageDataUrl(reader.result);
            setSelectedImageName(file.name);
        };
        reader.onerror = () => notify('error', 'Unable to read the selected image');
        reader.readAsDataURL(file);
    };

    const handleFileChange = (event) => {
        loadImage(event.target.files && event.target.files[0]);
        event.target.value = '';
    };

    const handleDrop = (event) => {
        event.preventDefault();
        loadImage(event.dataTransfer.files && event.dataTransfer.files[0]);
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            const payload = buildCoursePayload(formState, imageDataUrl);
            const result = await coursesServices.addCourses(payload, update ? id : undefined);
            if (!result || !result.status) {
                notify('error', (result && result.message) || 'Something went wrong');
                return;
            }

            const savedCourse = getResponseCourse(result);
            if (savedCourse) {
                setReadOnlyDetails(mapReadOnlyCourseFields(savedCourse));
                setDurationLabel(savedCourse.duration_label || durationLabel);
                setImageUrls((current) => ({
                    thumbnailUrl: savedCourse.thumbnail_url || savedCourse.thumbnail || current.thumbnailUrl,
                    fullUrl: savedCourse.imageUrl || current.fullUrl,
                    updatedAt: savedCourse.updated_at || savedCourse.last_update_on || current.updatedAt,
                }));
            }

            notify('success', result.message);
            history.push(Paths.Courses);
        } catch (error) {
            const message = error && error.response && error.response.data && error.response.data.message;
            notify('error', message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const formValidationCheck = () => {
        if (!formState.name || !formState.provider) {
            notify('error', REQUIRED_ERROR);
            return;
        }
        handleFormSubmit();
    };

    const selectedOrCurrentThumbnail = imageDataUrl || withCourseImageCacheBust(
        imageUrls.thumbnailUrl,
        imageUrls.updatedAt,
    );
    const selectedOrCurrentFullImage = imageDataUrl || withCourseImageCacheBust(
        imageUrls.fullUrl,
        imageUrls.updatedAt,
    );

    const renderTextField = ({ name, label, required = false, md = 4, multiline = false, rows }) => (
        <Grid item xs={12} md={md}>
            <TextField
                variant="outlined"
                fullWidth
                size="small"
                label={`${label}${required ? ' *' : ''}`}
                name={name}
                value={editableValue(formState[name])}
                multiline={multiline}
                minRows={rows}
                onChange={handleInputChange}
            />
        </Grid>
    );

    const renderReadOnlyField = (name, label, isDate = false) => (
        <Grid item xs={12} sm={6} md={3}>
            <TextField
                variant="outlined"
                fullWidth
                size="small"
                label={label}
                value={readOnlyValue(readOnlyDetails[name], isDate, name === 'is_moodle')}
                disabled
            />
        </Grid>
    );

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    {pageLoading ? <AppLoader minHeight={320} /> : (
                        <>
                            <div className={classes.pageHeader}>
                                <Typography variant="subtitle2" className={classes.heading} color="textPrimary">
                                    {update ? 'Update Course' : 'Create Course'}
                                </Typography>
                            </div>

                            <Section title="Basic" classes={classes}>
                                <Grid container spacing={3}>
                                    {renderTextField({ name: 'name', label: 'Name', required: true })}
                                    {renderTextField({ name: 'course_id', label: 'Course ID' })}
                                    <Grid item xs={12} md={4}>
                                        <OutlinedSelect
                                            val={formState.course_type}
                                            name="course_type"
                                            label="Course Type"
                                            variant="outlined"
                                            handleChange={(event) => handleChange(event.target.value, 'course_type')}
                                            styleOverrides={{ width: '100%', marginTop: 0 }}
                                            options={courseTypes}
                                        />
                                    </Grid>
                                    {renderTextField({ name: 'provider', label: 'Provider', required: true })}
                                    {renderTextField({ name: 'link', label: 'Link', md: 8 })}
                                    {renderTextField({ name: 'status', label: 'Status' })}
                                    {renderTextField({ name: 'category', label: 'Category' })}
                                    {renderTextField({ name: 'language', label: 'Language' })}
                                    {renderTextField({ name: 'level', label: 'Level' })}
                                    {renderTextField({ name: 'skill_level', label: 'Skill Level' })}
                                </Grid>
                            </Section>

                            <Section title="Catalog" classes={classes}>
                                <Grid container spacing={3}>
                                    {renderTextField({ name: 'domain', label: 'Domain' })}
                                    {renderTextField({ name: 'duration', label: 'Duration' })}
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            label="Duration label"
                                            value={durationLabel || '—'}
                                            disabled
                                            helperText="Formatted by the catalog service"
                                        />
                                    </Grid>
                                    {['popular', 'e_commerce', 'my_players'].map((field) => (
                                        <Grid item xs={12} md={4} key={field}>
                                            <TextField
                                                select
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                label={field === 'e_commerce' ? 'E-commerce' : field === 'my_players' ? 'My Players' : 'Popular'}
                                                name={field}
                                                value={booleanSelectValue(formState[field])}
                                                onChange={handleBooleanChange}
                                            >
                                                {booleanOptions.map((option) => (
                                                    <MenuItem key={option.value || 'unset'} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Section>

                            <Section title="Images" classes={classes}>
                                <Typography className={classes.helperText}>
                                    Upload one PNG or JPEG. The catalog image and list thumbnail are generated automatically.
                                </Typography>
                                <Grid container spacing={3} className={classes.imageGrid}>
                                    <Grid item xs={12} md={4}>
                                        <div className={classes.imageCard}>
                                            <span className={classes.imageLabel}>
                                                {imageDataUrl ? 'New thumbnail preview' : 'Current thumbnail'}
                                            </span>
                                            {selectedOrCurrentThumbnail ? (
                                                <img
                                                    className={classes.previewImage}
                                                    src={selectedOrCurrentThumbnail}
                                                    alt="Course thumbnail preview"
                                                />
                                            ) : <div className={classes.imageEmpty}>No course image</div>}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <div className={classes.imageCard}>
                                            <span className={classes.imageLabel}>
                                                {imageDataUrl ? 'New full image preview' : 'Current full image'}
                                            </span>
                                            {selectedOrCurrentFullImage ? (
                                                <img
                                                    className={classes.previewImage}
                                                    src={selectedOrCurrentFullImage}
                                                    alt="Full course preview"
                                                />
                                            ) : <div className={classes.imageEmpty}>No course image</div>}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <input
                                            id="course-image-input"
                                            className={classes.fileInput}
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            onChange={handleFileChange}
                                        />
                                        <label
                                            className={classes.dropzone}
                                            htmlFor="course-image-input"
                                            onDrop={handleDrop}
                                            onDragOver={(event) => event.preventDefault()}
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    document.getElementById('course-image-input').click();
                                                }
                                            }}
                                        >
                                            <strong>Choose or drop one image</strong>
                                            <span className={classes.helperText}>PNG or JPEG</span>
                                            {selectedImageName ? (
                                                <span className={classes.selectedFile}>{selectedImageName}</span>
                                            ) : null}
                                        </label>
                                    </Grid>
                                </Grid>
                            </Section>

                            <Section title="Content" classes={classes}>
                                <Grid container spacing={3}>
                                    {renderTextField({ name: 'description', label: 'Description', md: 12, multiline: true, rows: 5 })}
                                    {renderTextField({ name: 'long_description', label: 'Long Description', md: 12, multiline: true, rows: 4 })}
                                    {renderTextField({ name: 'course_overview', label: 'Course Overview', md: 12, multiline: true, rows: 4 })}
                                    {renderTextField({ name: 'key_features', label: 'Key Features', md: 6, multiline: true, rows: 4 })}
                                    {renderTextField({ name: 'skills_covered', label: 'Skills Covered', md: 6, multiline: true, rows: 4 })}
                                    {renderTextField({ name: 'benefits', label: 'Benefits', md: 6, multiline: true, rows: 4 })}
                                    {renderTextField({ name: 'eligibility', label: 'Eligibility', md: 6, multiline: true, rows: 4 })}
                                </Grid>
                            </Section>

                            <Section title="Integrations" classes={classes}>
                                <Grid container spacing={3}>
                                    {renderReadOnlyField('course_id_moodle', 'Moodle Course ID')}
                                    {renderReadOnlyField('course_id_talent_lms', 'TalentLMS Course ID')}
                                    {renderReadOnlyField('external_id', 'External ID')}
                                    {renderReadOnlyField('version', 'Version')}
                                    {renderReadOnlyField('is_moodle', 'Moodle course')}
                                </Grid>
                            </Section>

                            <Section title="Audit" classes={classes}>
                                <Grid container spacing={3}>
                                    {renderReadOnlyField('created_at', 'Created at', true)}
                                    {renderReadOnlyField('updated_at', 'Updated at', true)}
                                    {renderReadOnlyField('creation_date', 'Creation date', true)}
                                    {renderReadOnlyField('last_update_on', 'Last update', true)}
                                </Grid>
                            </Section>

                            <div className={classes.buttonRow}>
                                <Button
                                    onClick={() => history.push(Paths.Courses)}
                                    variant="outlined"
                                    color="default"
                                    className={classes.cancelButton}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    className={classes.button}
                                    onClick={formValidationCheck}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={15} color="inherit" /> : update ? 'Update' : 'Save'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </Layout>
    );
};

export default AddCourses;
